import React, { useState, useEffect } from "react";
import axios from "../utils/axios";

import {
  Paper,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Link as MuiLink,
} from "@mui/material";
import { Visibility, VisibilityOff, Person, Lock } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import LoginIcon from "@mui/icons-material/Login";
import { Link } from "react-router-dom";

function generateQuestion() {
  const ops = [
    { op: "+", fn: (a, b) => a + b },
    { op: "-", fn: (a, b) => a - b },
    { op: "x", fn: (a, b) => a * b },
  ];
  const a = Math.floor(Math.random() * 8) + 2;
  const b = Math.floor(Math.random() * 8) + 2;
  const opIdx = Math.floor(Math.random() * ops.length);
  const op = ops[opIdx];
  let question, answer;
  if (op.op === "-") {
    if (a < b) [question, answer] = [`${b} - ${a} = ?`, op.fn(b, a)];
    else [question, answer] = [`${a} - ${b} = ?`, op.fn(a, b)];
  } else {
    question = `${a} ${op.op} ${b} = ?`;
    answer = op.fn(a, b);
  }
  return { question, answer: answer.toString() };
}

function RegisterForm({ onRegister }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [security, setSecurity] = useState("");
  const [shake, setShake] = useState(false);

  useEffect(() => {
    randomNewQuestion();
    // eslint-disable-next-line
  }, []);

  const randomNewQuestion = () => {
    const q = generateQuestion();
    setQuestion(q.question);
    setAnswer(q.answer);
    setSecurity("");
  };

  const launchConfetti = () => {
    confetti({
      particleCount: 120,
      spread: 70,
      origin: { y: 0.7 },
      zIndex: 3000,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (password !== confirmPassword) {
      setMsgType("error");
      setMsg("Mật khẩu xác nhận không trùng khớp.");
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    if (security.trim() !== answer) {
      setMsgType("error");
      setMsg("Câu trả lời bảo mật không đúng.");
      setShake(true);
      setTimeout(() => setShake(false), 500);
      randomNewQuestion();
      return;
    }
    setLoading(true);
    try {
      // Tùy bạn giữ axios hoặc fetch như dự án bạn đang dùng!
      await axios.post(`/auth/register`, { username, password });
      setMsgType("success");
      setMsg("🎉 Đăng ký thành công! Bạn có thể đăng nhập.");
      setUsername("");
      setPassword("");
      setConfirmPassword("");
      randomNewQuestion();
      launchConfetti();
      if (onRegister) onRegister();
    } catch (err) {
      setMsgType("error");
      setMsg("Lỗi đăng ký: " + (err.response?.data?.message || err.message));
      setShake(true);
      setTimeout(() => setShake(false), 500);
      randomNewQuestion();
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -40, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 90, damping: 16 }}
      style={{ width: "100%", maxWidth: 380 }}
    >
      <motion.div
        animate={shake ? { x: [-7, 7, -7, 7, 0] } : { x: 0 }}
        transition={{ duration: 0.38 }}
      >
        <Paper
          elevation={6}
          sx={{
            px: 3,
            py: 3,
            borderRadius: "28px",
            width: 340,
            boxShadow: "0 8px 24px 0 rgba(100,160,255,0.13)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "#fff",
            margin: "auto",
          }}
        >
          <Typography
            variant="h5"
            align="center"
            mb={2}
            fontWeight={700}
            sx={{
              color: "#4169e1",
              letterSpacing: 0.5,
              fontSize: 23,
            }}
          >
            Đăng ký tài khoản
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              label="Tên đăng nhập"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
              autoComplete="username"
              size="medium"
              sx={{
                background: "#f8faff",
                borderRadius: 2,
              }}
            />
            <TextField
              label="Mật khẩu"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPw((show) => !show)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPw ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="medium"
              sx={{
                background: "#f8faff",
                borderRadius: 2,
              }}
            />
            <TextField
              label="Nhập lại mật khẩu"
              type={showConfirmPw ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPw((show) => !show)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showConfirmPw ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="medium"
              sx={{
                background: "#f8faff",
                borderRadius: 2,
              }}
            />
            <TextField
              label={`Câu hỏi bảo mật: ${question}`}
              value={security}
              onChange={(e) => setSecurity(e.target.value)}
              required
              autoComplete="off"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              size="medium"
              sx={{
                background: "#f8faff",
                borderRadius: 2,
              }}
            />
            <motion.div
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  mt: 1,
                  borderRadius: 99,
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: 2,
                  background: "linear-gradient(90deg, #4f8cff, #7a8cff 80%)",
                  ":hover": {
                    background: "linear-gradient(90deg, #7a8cff, #4f8cff 80%)",
                    boxShadow: 3,
                  },
                  height: 44,
                }}
                disabled={loading}
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{
                      repeat: Infinity,
                      duration: 1,
                      ease: "linear",
                    }}
                    style={{ display: "inline-block" }}
                  >
                    <CircularProgress size={22} />
                  </motion.div>
                ) : (
                  "Đăng ký"
                )}
              </Button>
            </motion.div>
            <AnimatePresence>
              {msg && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                >
                  <Alert
                    severity={msgType}
                    sx={{
                      mt: 1,
                      fontSize: "1rem",
                      borderRadius: 2,
                      fontWeight: 500,
                    }}
                  >
                    {msg}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          <Box
            mt={2}
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            <MuiLink
              component={Link}
              to="/login"
              underline="none"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#4169e1",
                fontWeight: 500,
                fontSize: 15,
                letterSpacing: 0.1,
                "&:hover": { color: "#2733d4", textDecoration: "underline" },
                transition: "0.2s",
                cursor: "pointer",
                gap: 0.5,
              }}
            >
              <LoginIcon sx={{ fontSize: 20, mr: 0.6 }} />
              Đã có tài khoản? Đăng nhập
            </MuiLink>
          </Box>
        </Paper>
      </motion.div>
    </motion.div>
  );
}

export default RegisterForm;
